import { useCallback, useEffect, useRef } from 'react';

import { Toast, useToastStore } from '@/stores/toast-store';

interface ToastItemProps {
  toast: Toast;
  onDismiss: (id: string) => void;
}

function ToastItem({ toast, onDismiss }: ToastItemProps) {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    timerRef.current = setTimeout(() => {
      onDismiss(toast.id);
    }, toast.duration);

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [toast.id, toast.duration, onDismiss]);

  const typeStyles = {
    success:
      'border-success bg-success/10 text-success dark:bg-success/10 dark:text-success',
    error:
      'border-danger bg-danger/10 text-danger dark:bg-danger/10 dark:text-danger',
    info:
      'border-accent-600 bg-accent-50 text-accent-900 dark:bg-accent-950 dark:text-accent-100 dark:border-accent-400',
  };

  const iconMap = {
    success: (
      <svg
        aria-hidden="true"
        className="h-5 w-5 text-success"
        fill="currentColor"
        viewBox="0 0 20 20"
      >
        <path
          fillRule="evenodd"
          d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
          clipRule="evenodd"
        />
      </svg>
    ),
    error: (
      <svg
        aria-hidden="true"
        className="h-5 w-5 text-danger"
        fill="currentColor"
        viewBox="0 0 20 20"
      >
        <path
          fillRule="evenodd"
          d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
          clipRule="evenodd"
        />
      </svg>
    ),
    info: (
      <svg
        aria-hidden="true"
        className="h-5 w-5 text-accent-600 dark:text-accent-400"
        fill="currentColor"
        viewBox="0 0 20 20"
      >
        <path
          fillRule="evenodd"
          d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
          clipRule="evenodd"
        />
      </svg>
    ),
  };

  return (
    <div
      role="alert"
      className={`flex items-start gap-3 rounded-lg border-l-4 p-4 shadow-lg ${typeStyles[toast.type]}`}
    >
      <span className="flex-shrink-0">{iconMap[toast.type]}</span>
      <p className="flex-1 text-sm font-medium">{toast.message}</p>
      <button
        type="button"
        onClick={() => onDismiss(toast.id)}
        className="flex-shrink-0 rounded p-1 opacity-70 hover:opacity-100 focus:outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-current"
        aria-label="Dismiss notification"
      >
        <svg
          aria-hidden="true"
          className="h-4 w-4"
          fill="currentColor"
          viewBox="0 0 20 20"
        >
          <path
            fillRule="evenodd"
            d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
            clipRule="evenodd"
          />
        </svg>
      </button>
    </div>
  );
}

/**
 * Toast notification system that displays stacked notifications in the bottom-right corner.
 * - Max 3 visible toasts (managed by the toast store's FIFO eviction)
 * - Auto-dismiss: 5s for success/info, 8s for error
 * - Manual dismiss via X button
 * - ARIA live regions: assertive for errors, polite for success/info
 * - Stacked vertically, newest at bottom
 */
export function ToastSystem() {
  const toasts = useToastStore((state) => state.toasts);
  const removeToast = useToastStore((state) => state.removeToast);

  const handleDismiss = useCallback(
    (id: string) => {
      removeToast(id);
    },
    [removeToast],
  );

  // Separate toasts by ARIA politeness level
  const errorToasts = toasts.filter((t) => t.type === 'error');
  const nonErrorToasts = toasts.filter((t) => t.type !== 'error');

  return (
    <div className="fixed bottom-4 right-4 z-50 flex w-full max-w-sm flex-col gap-2">
      {/* ARIA live region for error toasts - assertive */}
      <div aria-live="assertive" aria-atomic="false" className="flex flex-col gap-2">
        {errorToasts.map((toast) => (
          <ToastItem key={toast.id} toast={toast} onDismiss={handleDismiss} />
        ))}
      </div>

      {/* ARIA live region for success/info toasts - polite */}
      <div aria-live="polite" aria-atomic="false" className="flex flex-col gap-2">
        {nonErrorToasts.map((toast) => (
          <ToastItem key={toast.id} toast={toast} onDismiss={handleDismiss} />
        ))}
      </div>
    </div>
  );
}
